import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  TextField,
  InputAdornment,
} from "@mui/material";
import CommonModal from "../../../components/common/CommonModal";
import { useSessions, SessionData } from "../../../hooks/useSession";
import { useAuth } from "../../../contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import EnhancedTable from "../../../components/common/EnhancedTable";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import SessionFormModal from "../../../components/Modals/SessionFormModal";
import { useTranslation } from "react-i18next";
import { useApiError } from "../../../hooks/useApiError";
import { useError } from "../../../contexts/ErrorContext";
import { red } from "@mui/material/colors";
import { useClasses } from "../../../hooks/useClasses";
import { useStudents } from "../../../hooks/useStudents";

const SessionManagement: React.FC = () => {
  const { user, isTeacher, isSchoolAdmin } = useAuth();

  // For both teachers and school admins, use their schoolId directly from user context
  // This ensures we use the actual schoolId from the logged-in user
  const schoolId = user?.schoolId;


  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const {
    sessionsQuery,
    createSessionMutation,
    deleteSessionMutation,
    updateSessionMutation,
  } = useSessions(schoolId ?? undefined);

  const { handleApiError } = useApiError();
  const { showError } = useError();

  // Fetch related data to check dependencies before deletion
  const { classesQuery } = useClasses(schoolId ?? undefined);
  const { studentsQuery } = useStudents();

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentSession, setCurrentSession] = useState<SessionData | undefined>(
    undefined
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<SessionData | null>(null);
  const [searchTerm, setSearchTerm] = useState("");


  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentSession(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (sessionData: SessionData) => {
    // Once a session is created (active or inactive), the session name cannot be edited
    // Only active/inactive status can be changed
    // Always disable session name editing for existing sessions
    setIsEdit(true);
    // Remove the status field before passing to the modal as it's only for display
    const { status, ...cleanSessionData } = sessionData as any;
    // Always set hasDependencies to true for existing sessions to disable name editing
    (cleanSessionData as any).hasDependencies = true;
    setCurrentSession(cleanSessionData);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = (formData: SessionData) => {
    if (isEdit && formData.id) {
      // Filter out the status and hasDependencies fields from the payload as they're only for display/logic
      const { status, hasDependencies, ...updateData } = formData as any;

      updateSessionMutation.mutate(
        { id: formData.id, data: updateData },
        {
          onSuccess: () => {
            handleCloseModal();
          },
          onError: (error: unknown) => {
            const err = error as any;
            const backendMessage = err?.response?.data?.message;
            showError(backendMessage || t("error_updating_session"));
          },
        }
      );
    } else {
      createSessionMutation.mutate(formData, {
        onSuccess: () => {
          handleCloseModal();
        },
        onError: (error: unknown) => {
          const err = error as any;
          const backendMessage = err?.response?.data?.message;
          showError(backendMessage || t("error_saving_session"));
        },
      });
    }
  };

  const handleOpenDeleteDialog = (id: number) => {

    // Find the session data
    const sessionData = sessionsQuery.data?.find((session) => session.id === id);
    if (!sessionData) {
      showError(t("session_not_found"));
      return;
    }

    // Check if there are students in this session
    const studentsInSession = studentsQuery.data?.filter(
      (student) => student.sessionId === id && student.status !== false
    ) || [];

    // Prevent deletion if there are students
    if (studentsInSession.length > 0) {
      showError(
        `${t("cannot_delete_session_with_students")} "${sessionData.name}". ${t("there_are")}: ${studentsInSession.length} ${t("student")}(s). ${t("please_remove_students_before_deleting_session")}`
      );
      return;
    }

    // If no students, allow deletion
    setSessionToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (sessionToDelete !== null) {
      deleteSessionMutation.mutate(sessionToDelete, {
        onSuccess: () => {
          handleCloseDeleteDialog();
        },
        onError: (error: unknown) => {
          // Close the dialog first
          handleCloseDeleteDialog();

          const err = error as any;
          const backendMessage = err?.response?.data?.message || err?.message || "";
          const sessionData = sessionsQuery.data?.find((session) => session.id === sessionToDelete);
          const sessionName = sessionData?.name || "this session";

          // Check if error is related to foreign key constraint with subjects
          const errorString = JSON.stringify(error).toLowerCase();
          if (errorString.includes("fk_subject_session") || errorString.includes("subjects") || backendMessage.includes("subjects")) {
            showError(
              `${t("cannot_delete_session_with_subjects")} "${sessionName}". ${t("this_session_contains_subjects")}. ${t("please_remove_all_subjects_before_deleting_session")}`
            );
          } else {
            // For other foreign key constraints, try to extract meaningful error
            if (backendMessage.includes("foreign key") || backendMessage.includes("violates foreign key constraint")) {
              showError(
                `${t("cannot_delete_session_with_dependencies")} "${sessionName}". ${t("it_is_still_being_referenced_by_other_data")}. ${t("please_remove_all_associated_data_before_deleting_session")}`
              );
            } else {
              showError(backendMessage || t("error_deleting_session"));
            }
          }
        },
      });
    }
  };



  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: SessionData
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  // Helper function to check if a session has students (for delete functionality)
  const sessionHasStudentsSubjectsOrApplications = (sessionId: number | undefined): boolean => {
    if (!sessionId) return false;

    // Check if data is still loading
    if (studentsQuery.isLoading) {
      return false; // Assume no dependencies while loading
    }

    const studentsInSession = studentsQuery.data?.filter(
      (student) => student.sessionId === sessionId && student.status !== false
    ) || [];

    return studentsInSession.length > 0;
  };

  const columns = [
    { id: "name", label: t("session_name"), minWidth: 170 },
    { id: "status", label: t("status"), minWidth: 100 },

  ];

  const renderActions = (row: SessionData) => (
    <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
      <MoreVertIcon fontSize="small" />
    </IconButton>
  );

  const formattedData = (sessionsQuery.data || [])
    .map((session, index) => ({
      ...session,
      startDate: "",
      endDate: "",
      status: session.isActive ? t("active") : t("inactive"),
    }))
    .sort((a, b) => {
      // Sort active sessions first, then inactive sessions
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      // If both have same active status, sort by name
      return a.name.localeCompare(b.name);
    });

  const filteredData = formattedData.filter((row) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase().trim();
    return (
      row.name.toLowerCase().includes(searchLower) ||
      row.status.toLowerCase().includes(searchLower)
    );
  });

  // Show error if no school ID found
  if (!schoolId) {
    return (
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
          <Typography variant="body1" color="error">
            {t("no_school_found_for_admin")}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography 
            variant="h6" 
            fontWeight={700}
            sx={{ 
              fontSize: '1.25rem',
              lineHeight: 1.2,
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            {t("session_management")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
            />
            <Button
              variant="contained"
              size="small"
              sx={{ 
                backgroundColor: "#3092e3", 
                "&:hover": { backgroundColor: "#1976d2" },
                height: 36,
              }}
              onClick={handleOpenAddModal}
            >
              {t("add_session")}
            </Button>
          </Box>
        </Box>
        <EnhancedTable
          columns={columns}
          data={filteredData}
          searchFields={[]}
          actions={renderActions}
          cardProps={{
            elevation: 0,
            sx: {
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
              mb: 0,
              '& .MuiCardContent-root': {
                p: 0,
                '&:last-child': { pb: 0 }
              }
            }
          }}
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuRow) handleOpenEditModal(menuRow);
            handleMenuClose();
          }}
          disabled={isTeacher() === true}
          sx={{
            '&.Mui-disabled': {
              opacity: 0.6
            }
          }}
        >
          {t("edit")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) handleOpenDeleteDialog(menuRow.id!);
            handleMenuClose();
          }}
          disabled={menuRow ? sessionHasStudentsSubjectsOrApplications(menuRow.id) : false}
          sx={{
            color: menuRow && sessionHasStudentsSubjectsOrApplications(menuRow.id) ? 'text.disabled' : 'error.main',
            '&.Mui-disabled': {
              opacity: 0.6
            }
          }}
        >
          {t("delete")}
        </MenuItem>
      </Menu>

      <SessionFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        initialData={currentSession}
        onSubmit={handleSubmit}
        isEdit={isEdit}
        schoolId={schoolId ?? 0}
      />

      <CommonModal
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        title={t("confirm_delete")}
        maxWidth="sm"
        onSubmit={handleConfirmDelete}
        submitLabel={t("delete")}
        danger
      >
        <Typography variant="body1" color="text.secondary">
          {t("are_you_sure_you_want_to_delete_this_session")}
        </Typography>
      </CommonModal>
    </>
  );
};

export default SessionManagement;
